import { type FC, useState, useMemo } from "react";

import {
  ArrowRightIcon,
  Box,
  Button,
  ButtonGroup,
  CloseIcon,
  Column,
  Combobox,
  HiddenFormLabel,
  IconButton,
  Row,
  TextInput,
  Tooltip,
  WandIcon,
} from "@hightouch/htui";
import get from "lodash/get";

import { useDestinationForm } from "src/contexts/destination-form-context";
import { usePermissionContext } from "src/components/permission/permission-context";
import { isColumnReference } from "src/types/visual";
import { flattenOptions } from "src/ui/select";
import { automap, suggest } from "src/utils/automap";
import { NEW_ICON_MAP } from "src/utils/destinations";
import { ArrayInlineMapper } from "./array-inline-mapper";
import {
  type ColumnOption,
  useFormkitContext,
} from "src/formkit/components/formkit-context";
import { SyncType } from "src/formkit/components/sync-type";
import {
  type ArrayMapping,
  type InlineMapperProps,
  isArrayMapping,
  type Mapping,
  MappingType,
  type ObjectMapping,
  type Option,
  type SelectedOptionContext,
} from "src/formkit/components/types";
import {
  isAudienceTrait,
  resolveFromValue,
} from "src/formkit/components/utils";
import { Mapper } from "./mapper";
import {
  MAPPING_BUTTONS_INSET,
  MAPPING_COLUMN_GAP,
  MAPPING_ROW_COLUMNS,
  MAPPING_SOURCE_INSET,
} from "./utils";

const PANEL_PADDING = 4;

/**
 * Populates mappings with fields that were mapped previously and required fields that are not mapped.
 */
const generateMappings = (
  value: ArrayMapping | ObjectMapping,
  properties: SelectedOptionContext[] | undefined,
): Mapping[] => {
  const requiredOptions: Mapping[] = [];
  const children = isArrayMapping(value) ? value?.children : value?.from;
  let mappings: Mapping[] = [];
  if (Array.isArray(properties)) {
    for (const option of properties) {
      if (option.required) {
        requiredOptions.push({
          to: option.value,
          type: MappingType.STANDARD,
        });
      }
    }
  }
  if (Array.isArray(children)) {
    const nestedMappings = new Set(children.map(({ to }) => to));
    mappings = [
      ...children,
      ...requiredOptions.filter(({ to }) => !nestedMappings.has(to)),
    ];
  } else {
    mappings = requiredOptions;
  }

  // If there is nothing mapped previously and no required fields, initiate the mappings with one standard field for clarity.
  if (mappings.length === 0) {
    mappings.push({
      type: MappingType.STANDARD,
    });
  }
  return mappings;
};

export const InlineMapper: FC<Readonly<InlineMapperProps>> = ({
  currentDepth,
  enabledNestedInlineMapper,
  errorPrefix,
  columnOptionsOverride,
  jsonColumnProperties,
  onChange,
  onChangeJsonColumnProperties,
  onReloadEligibleInlineMapperColumns,
  overwriteColumnsWithArrayProps,
  parentMapping,
  required,
  retrieveErrorMessage,
  selectedOptionContext,
  supportOnlyWriteIfNull,
  templates,
  type,
  value,
  isDisabled = false,
}) => {
  const [isSuggested, setIsSuggested] = useState(false);

  const { errors, sourceDefinition } = useDestinationForm();

  const jsonColumnPropsToOptions = (): ColumnOption[] | undefined => {
    if (!jsonColumnProperties.selectedColumnProps) {
      return undefined;
    }

    return Array.from(jsonColumnProperties.selectedColumnProps).map((prop) => {
      return {
        label: prop,
        value: prop,
        type: "array_property",
      };
    });
  };

  const {
    columns: contextColumns,
    destinationDefinition,
    model,
  } = useFormkitContext();
  const columns =
    columnOptionsOverride ??
    (overwriteColumnsWithArrayProps
      ? jsonColumnPropsToOptions()
      : contextColumns);
  const permission = usePermissionContext();

  // Get the current mappings
  const mappings = useMemo(() => {
    return generateMappings(value, selectedOptionContext);
  }, [value, selectedOptionContext]);

  const isOptionExcluded = (option: Option) => {
    const valueAlreadyMapped = mappings.some(
      (mapping) =>
        mapping?.to ===
        (isColumnReference(option.value) ? option.label : option.value),
    );
    return valueAlreadyMapped;
  };

  const suggestColumns = () => {
    const flatColumns = flattenOptions(columns || []);
    const unmappedOptions = (
      !selectedOptionContext?.length
        ? (flatColumns as any[])
        : selectedOptionContext
    ).filter((option) => !isOptionExcluded(option));
    setIsSuggested(true);
    const nonSourceOnlyColumns = (columns || []).filter(
      (column) => !column.entity?.isJourneyContextVariable,
    );
    return automap(nonSourceOnlyColumns, unmappedOptions as any[]);
  };

  const append = (newMapping: Mapping[]) => {
    onChange([...mappings, ...newMapping]);
  };

  const change = (index: number, newMapping: Mapping) => {
    const mappingsCopy = [...mappings];
    mappingsCopy[index] = newMapping;
    onChange(mappingsCopy);
  };

  const remove = (index: number) => {
    const mappingsCopy = [...mappings];
    mappingsCopy.splice(index, 1);
    onChange(mappingsCopy);
  };

  const panelOffset = PANEL_PADDING * (currentDepth - 1);
  const fieldsInset = {
    ml: MAPPING_SOURCE_INSET,
    pl: PANEL_PADDING * currentDepth,
  };

  return (
    <Column
      // The panel is painted behind the rows so they keep the full-width grid of the mapping row above.
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        bottom: 0,
        left: MAPPING_SOURCE_INSET,
        ml: panelOffset,
        right: -2,
        zIndex: -1,
        bg: "base.background",
        borderColor: "base.border",
        borderLeftStyle: "solid",
        borderLeftWidth: "2px",
        borderRightRadius: "lg",
      }}
      gap={3}
      isolation="isolate"
      position="relative"
      py={4}
      width="100%"
    >
      {mappings.map((mapping, index) => {
        const { to, type } = mapping;
        const selectedOption = selectedOptionContext?.find(
          (option) => option.value == to,
        );
        const isMappingRequired = Boolean(selectedOption?.required);
        const destinationFieldOptions = selectedOptionContext?.map((option) => {
          if (isOptionExcluded(option)) {
            return { ...option, disabled: true };
          } else {
            return option;
          }
        });
        // If mapping destination is configured, but it's not in the list of
        // available options, it's either a custom value typed by the user or
        // it comes from a suggested mapping
        if (typeof to === "string" && !selectedOption) {
          destinationFieldOptions?.push({
            label: to,
            value: to,
            disabled: false,
          });
        }

        const errorMessage = retrieveErrorMessage(errors, index, errorPrefix);

        return (
          <Box
            alignItems="center"
            columnGap={MAPPING_COLUMN_GAP}
            display="grid"
            gridTemplateColumns={MAPPING_ROW_COLUMNS}
            key={to ?? index}
            rowGap={1}
          >
            <Box minWidth={0} {...fieldsInset}>
              <Mapper
                columnOptions={columns}
                enableInLineMapper={enabledNestedInlineMapper}
                isDisabled={isDisabled || permission.unauthorized}
                isError={!!errors?.[`${errorPrefix}[${index}].from`]}
                jsonColumnProperties={jsonColumnProperties}
                overwriteColumnsWithArrayProps={overwriteColumnsWithArrayProps}
                parentMapping={parentMapping ? parentMapping : value}
                placeholder={`Select a field from ${sourceDefinition?.name ?? "the source"}`}
                selectedOption={selectedOption}
                templates={templates ?? []}
                value={mapping}
                onReloadEligibleInlineMapperColumns={
                  onReloadEligibleInlineMapperColumns
                }
                onChange={(option) => {
                  const availableOptions = destinationFieldOptions?.filter(
                    (o) => !o.disabled,
                  );
                  if (
                    !to &&
                    availableOptions?.length &&
                    option?.type === MappingType.STANDARD
                  ) {
                    const fieldName =
                      option.alias ?? resolveFromValue(option.from);
                    change(
                      index,
                      suggest(
                        { label: fieldName, value: option.from },
                        availableOptions,
                      ) as Mapping,
                    );
                  } else {
                    change(index, {
                      ...option,
                      to,
                    });
                  }
                }}
                onChangeJsonColumnProperties={onChangeJsonColumnProperties}
              />
            </Box>
            <Column gap={2} fontSize="xl" color="text.tertiary">
              <ArrowRightIcon />
            </Column>
            <Row alignItems="center" gap={2}>
              <Column flex={1} minWidth={0}>
                {selectedOptionContext ? (
                  <HiddenFormLabel
                    label={`Select or enter a field from ${destinationDefinition?.name}`}
                  >
                    <Combobox
                      supportsCreatableOptions
                      createOptionMessage={(inputValue) =>
                        `Create field "${inputValue}"`
                      }
                      emptyOptionsMessage="Type in a field name and Hightouch will create that field in the destination"
                      isDisabled={
                        isDisabled ||
                        permission.unauthorized ||
                        isMappingRequired
                      }
                      isInvalid={!!errors?.[`${errorPrefix}[${index}].to`]}
                      isOptionDisabled={(option) => {
                        // For some reason when option is selected, it becomes disabled
                        // and has `disabled = true` property, so we need to take this into account
                        // and make sure selected option can't be disabled
                        const isSelectedOption = option.value === to;
                        if (isSelectedOption) {
                          return false;
                        }

                        return Boolean(option.disabled);
                      }}
                      optionAccessories={(option) => {
                        return option.type
                          ? {
                              leftAccessory: {
                                type: "icon",
                                icon: NEW_ICON_MAP[option.type],
                              },
                            }
                          : undefined;
                      }}
                      optionLabel={(option) => {
                        return option.object?.label
                          ? `${option.label} (${option.object.label})`
                          : option.label;
                      }}
                      options={destinationFieldOptions || []}
                      placeholder={
                        to
                          ? ""
                          : `Select or enter a field from ${destinationDefinition?.name}`
                      }
                      value={to}
                      width="100%"
                      onChange={(value) => {
                        const option = destinationFieldOptions?.find(
                          (option) => option.value === value,
                        );
                        if (
                          option?.type === "OBJECT" &&
                          enabledNestedInlineMapper
                        ) {
                          change(index, {
                            from: undefined,
                            to: option?.value ?? "",
                            type: MappingType.OBJECT,
                          });
                        } else if (
                          option?.type === "array" &&
                          enabledNestedInlineMapper
                        ) {
                          change(index, {
                            from: undefined,
                            to: option?.value ?? "",
                            type: MappingType.ARRAY,
                            children: [],
                          });
                        } else {
                          change(index, {
                            ...mapping,
                            to: option?.value ?? "",
                          });
                        }
                      }}
                      onCreateOption={(inputValue) => {
                        change(index, {
                          ...mapping,
                          to: inputValue ?? "",
                        });
                      }}
                    />
                  </HiddenFormLabel>
                ) : (
                  <InputField
                    destinationName={destinationDefinition?.name || ""}
                    disabled={isDisabled || permission.unauthorized}
                    error={!!errors?.[`${errorPrefix}[${index}].to`]}
                    value={to ?? ""}
                    onBlur={(value) => {
                      change(index, {
                        ...mapping,
                        to: value,
                      });
                    }}
                  />
                )}
              </Column>
              <SyncType
                isDisabled={
                  permission.unauthorized ||
                  ["array", "object", "reference"].includes(type)
                }
                setValue={(val) => change(index, val)}
                supportOnlyWriteIfNull={supportOnlyWriteIfNull}
                value={mapping}
              />
              {required && mappings.length <= 1 ? (
                // keeps destination fields the same width across rows
                <Row minHeight="32px" width="32px" />
              ) : isMappingRequired ? (
                <Row
                  alignItems="center"
                  height="20px"
                  justifyContent="center"
                  width="32px"
                >
                  <Tooltip message="Required">
                    <Box color="danger.500" fontSize={18}>
                      *
                    </Box>
                  </Tooltip>
                </Row>
              ) : (
                <Tooltip message="Remove mapping">
                  <IconButton
                    aria-label="Remove mapping"
                    icon={CloseIcon}
                    isDisabled={permission.unauthorized}
                    onClick={() => {
                      remove(index);
                    }}
                  />
                </Tooltip>
              )}
            </Row>
            {errorMessage && (
              <Box
                color="danger.base"
                gridColumn="1 / 4"
                {...fieldsInset}
                pr={MAPPING_BUTTONS_INSET}
              >
                {errorMessage}
              </Box>
            )}
            {type === "object" && enabledNestedInlineMapper && (
              <Box display="grid" gridColumn="1 / 4">
                <InlineMapper
                  currentDepth={currentDepth + 1}
                  enabledNestedInlineMapper={currentDepth < 2}
                  errorPrefix={`${errorPrefix}[${index}].from`}
                  columnOptionsOverride={columnOptionsOverride}
                  jsonColumnProperties={jsonColumnProperties}
                  outerContainerSx={{ marginTop: "0px" }}
                  overwriteColumnsWithArrayProps={
                    overwriteColumnsWithArrayProps
                  }
                  retrieveErrorMessage={retrieveErrorMessage}
                  parentMapping={parentMapping ? parentMapping : value}
                  selectedOptionContext={selectedOption?.["properties"]}
                  templates={templates ?? []}
                  type="object"
                  value={mapping}
                  onReloadEligibleInlineMapperColumns={
                    onReloadEligibleInlineMapperColumns
                  }
                  onChange={(value) => {
                    change(index, {
                      ...mapping,
                      from: value,
                      type: MappingType.OBJECT,
                    });
                  }}
                  onChangeJsonColumnProperties={onChangeJsonColumnProperties}
                />
              </Box>
            )}
            {type === "array" && enabledNestedInlineMapper && (
              <Box display="grid" gridColumn="1 / 4">
                <ArrayInlineMapper
                  currentDepth={currentDepth + 1}
                  enabledNestedInlineMapper={currentDepth < 2}
                  errorPrefix={`${errorPrefix}[${index}].from`}
                  columnOptionsOverride={columnOptionsOverride}
                  jsonColumnProperties={jsonColumnProperties}
                  outerContainerSx={{ marginTop: "0px" }}
                  retrieveErrorMessage={retrieveErrorMessage}
                  parentMapping={parentMapping ? parentMapping : value}
                  selectedOptionContext={selectedOption?.["properties"]}
                  templates={templates ?? []}
                  value={mapping}
                  onChange={(value) => {
                    change(index, {
                      ...mapping,
                      children: value,
                      type: MappingType.ARRAY,
                    });
                  }}
                  onChangeJsonColumnProperties={onChangeJsonColumnProperties}
                  onReloadEligibleInlineMapperColumns={
                    onReloadEligibleInlineMapperColumns
                  }
                />
              </Box>
            )}
          </Box>
        );
      })}
      <Box {...fieldsInset}>
        <ButtonGroup>
          <Button
            isDisabled={permission.unauthorized}
            onClick={() => {
              append([{ type: MappingType.STANDARD }]);
            }}
          >
            {type === "array" ? "Add array mapping" : "Add object mapping"}
          </Button>
          {(model || (columns?.length ?? 0) > 0) && (
            <Tooltip
              isDisabled={permission.unauthorized}
              message={
                isSuggested
                  ? "All possible suggestions applied"
                  : "Suggest mappings"
              }
            >
              <IconButton
                aria-label="Suggest mappings"
                icon={WandIcon}
                isDisabled={permission.unauthorized}
                variant="secondary"
                onClick={() => {
                  append(suggestColumns() as Mapping[]);
                }}
              />
            </Tooltip>
          )}
        </ButtonGroup>
      </Box>
    </Column>
  );
};

export const isTraitlike = (value: unknown): boolean => {
  return (
    isAudienceTrait(get(value, "type")) || get(value, "column.type") === "trait"
  );
};

const InputField: FC<
  Readonly<{
    destinationName: string;
    disabled: boolean;
    error: boolean;
    onBlur: (value: string) => void;
    value: string;
  }>
> = ({ destinationName, disabled, error, onBlur, value }) => {
  const [inputValue, setInputValue] = useState(value || "");

  /**
   * We use onBlur to update the nested mapping state. This is because if we use onChange in the `Input` component,
   * it will cause re-rendering and loss of focus on the input field whenever there is a change.
   * Since we are changing the nested mapping state onBlur, we cannot use the nested mapping's `to` as a value since it is not updated
   * whenever a user types.
   * This requires every `Input` component to have its own state that it references and we pass that state back once onBlur is called.
   */
  return (
    <HiddenFormLabel label={`Enter a field from ${destinationName}`}>
      <TextInput
        isDisabled={disabled}
        isInvalid={error}
        placeholder={`Enter a field from ${destinationName}`}
        value={inputValue}
        width="100%"
        onBlur={() => onBlur(inputValue)}
        onChange={(event) => setInputValue(event.target.value)}
      />
    </HiddenFormLabel>
  );
};
